import {useTranslation} from 'next-i18next'
import {FormProvider, useForm} from 'react-hook-form'
import {Flex, Text} from '@chakra-ui/react'
import {useEffect} from 'react'
import * as yup from 'yup'
import {yupResolver} from '@hookform/resolvers/yup'
import {Radio} from '_app/components'
import {AcceptRegulations} from '_app/components/AcceptRegulations'
import ShippingMethodLabel from '_app/components/shared/ShippingMethodLabel'
import {useShippingMethod} from './useShippingMethod'
import {PickupMethodDetails} from './PickupMethodDetails'

const schema = yup.object().shape({
    delivery_option: yup.string().required('errors.mixed.required'),
    inpost_point: yup.string().when('delivery_option', {
        is: (val: string) => val === 'inpostlocker',
        then: schema => schema.required('errors.mixed.required'),
    }),
    showroom: yup.string().when('delivery_option', {
        is: (val: string) => val === 'pickup',
        then: schema => schema.required('errors.mixed.required'),
    }),
    accept_regulations: yup
        .boolean()
        .required('errors.checkbox.conditions')
        .oneOf([true], 'errors.checkbox.conditions'),
})

interface Props {
    setValidForm: (val: boolean) => void;
}

export const ShippingMethod = ({setValidForm}: Props) => {
    const {t} = useTranslation()
    const {
        shippingMethods,
        setShippingMethodsOnCart,
        setShippingAddressOnCart,
    } = useShippingMethod()
    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            delivery_option: shippingMethods.courier[0]?.carrier_code,
            accept_regulations: false,
            inpost_point: '',
            showroom: '',
        },
        mode: 'onChange',
    })
    const {formState, watch, setValue} = methods
    const {delivery_option, showroom, inpost_point} = watch()

    useEffect(() => {
        if (shippingMethods.courier[0]?.carrier_code) {
            setValue('delivery_option', shippingMethods.courier[0].carrier_code)
        }
    }, [shippingMethods.courier[0]?.carrier_code])

    useEffect(() => {
        setValidForm(false)
        if (formState.isValid && !formState.isValidating) {
            setShippingMethodsOnCart({
                delivery_option,
                showroom,
                inpost_point,
            }).then(() => setValidForm(true))
        }
    }, [
        formState.isValid,
        formState.isValidating,
        delivery_option,
        showroom,
        inpost_point,
        setValidForm,
        setShippingMethodsOnCart,
    ])

    const selectedMethod = watch('delivery_option')

    return (
        <Flex gap={4} flexDirection="column">
            <Text fontWeight={600}>{t('SELECT_DELIVERY_METHOD')}</Text>

            <FormProvider {...methods}>
                <form>
                    {!!shippingMethods.pickup.length ? (
                        <Radio
                            name="delivery_option"
                            label={t('DELIVERY_TO_PICKUP_POINT')}
                            options={shippingMethods.pickup.map(method => ({
                                label: <ShippingMethodLabel method={method}/>,
                                value: method.carrier_code,
                                body: (
                                    <PickupMethodDetails
                                        show={selectedMethod === method.carrier_code}
                                        carrierCode={method.carrier_code}
                                        onAddressChange={setShippingAddressOnCart}
                                    />
                                ),
                            }))}
                        />
                    ) : null}

                    {!!shippingMethods.courier.length ? (
                        <Radio
                            name="delivery_option"
                            label={t('COURIER_DELIVERY')}
                            options={shippingMethods.courier.map(method => ({
                                label: <ShippingMethodLabel method={method}/>,
                                value: method.carrier_code,
                            }))}
                        />
                    ) : null}

                    <AcceptRegulations name="accept_regulations" mt={8}/>
                </form>
            </FormProvider>
        </Flex>
    )
}
